import { useState, useEffect } from "react";
import { Key, ArrowRight, ArrowLeft, Eye, Ear, Hand, Wind, Sparkles, Lock, Check, Camera } from "lucide-react";
import axios from "axios";
import { SEO, API } from "./components";

const FINANCIAL_FREEDOM = {
  productId: "financial-freedom",
  storageKey: "sf_ff",
  brand: "Financial Freedom",
  headline: "Financial Freedom",
  subheadline: "Snapshot",
  welcomeH1: <>Your <em>Target Financial</em> Snapshot</>,
  welcomeQuote: "One question. One answer. Could change everything.",
  welcomeBody: [
    "You are about to enter a journey of reflection and self-discovery. Each petal is designed to help you uncover patterns, perspectives, and possibilities within your relationship with money, financial decisions, and the goals you want to achieve.",
  ],
  petals: [
    {
      id: "gold",
      color: "Gold",
      theme: "Abundance · Vision · Possibility",
      accent: "#D4A017",
      accentDeep: "#8B6914",
      accentBg: "linear-gradient(135deg, #FFF3D1 0%, #E5B824 55%, #A67B0F 100%)",
      prompts: [
        { icon: "eye", label: "Visual", think: "Think about having everything you've ever wanted financially. Hold that thought while you take your selfie.", q1: "What emotion did you capture?", q2: "What do you think created that emotion?" },
        { icon: "ear", label: "Auditory", think: "Think about the loudest belief you've carried about money. Hold that thought while you take your selfie.", q1: "Where do you think that belief came from?", q2: "Is it still serving you today?" },
        { icon: "hand", label: "Kinesthetic", think: "Think about achieving complete financial freedom. Hold that thought while you take your selfie.", q1: "Where did your body respond first?", q2: "What do you think it was trying to tell you?" },
        { icon: "wind", label: "Olfactory", think: "Think about your greatest financial success. What familiar scents do you remember? Fresh coffee? Ocean breeze? Perfume? Hold that memory while you take your selfie.", q1: "What scent came to mind first?", q2: "Why do you think that memory returned?" },
        { icon: "sparkles", label: "Taste", think: "Think about what prosperity would taste like if you experienced it today. Hold that thought while you take your selfie.", q1: "Why did you choose that flavour?", q2: "What does it reveal about your relationship with abundance?" },
        { icon: "key", label: "Intuitive", think: "Looking at your selfie, what opportunity have you been ignoring? What is your intuition asking you to trust?", q1: "What keeps drawing you back to it?", q2: "What would happen if you trusted yourself this time?" },
      ],
    },
    {
      id: "emerald",
      color: "Emerald Green",
      theme: "Growth · Action · Opportunity",
      accent: "#2AA84A",
      accentDeep: "#1D7A34",
      accentBg: "linear-gradient(135deg, #E8F7EC 0%, #6BC17B 100%)",
      prompts: [
        { icon: "eye", label: "Visual", think: "Think about the financial habit that has had the greatest impact on your life. Hold that thought while you take your selfie.", q1: "What emotion did you capture?", q2: "Is that habit helping you grow or keeping you where you are?" },
        { icon: "ear", label: "Auditory", think: "Think about the advice you've followed most often when making financial decisions. Hold that thought while you take your selfie.", q1: "Do you still believe that advice is right for you?", q2: "If not, what would you choose differently today?" },
        { icon: "hand", label: "Kinesthetic", think: "Think about taking one meaningful step toward your financial goal today. Hold that thought while you take your selfie.", q1: "What feeling showed up first?", q2: "What do you think that feeling is trying to protect?" },
        { icon: "wind", label: "Olfactory", think: "Think about a time when your hard work paid off. Hold that thought while you take your selfie.", q1: "What scent came to mind?", q2: "What does that memory remind you that you're capable of?" },
        { icon: "sparkles", label: "Taste", think: "Think about the rewards of building long-term financial security. Hold that thought while you take your selfie.", q1: "What flavour came to mind?", q2: "What does it reveal about what success means to you?" },
        { icon: "key", label: "Intuitive", think: "Think about one financial decision you've been postponing. Hold that thought while you take your selfie.", q1: "What is your intuition encouraging you to do?", q2: "What's the biggest reason you haven't done it yet?" },
      ],
    },
    {
      id: "purple",
      color: "Royal Purple",
      theme: "Prosperity · Belief · Transformation",
      accent: "#8E44D4",
      accentDeep: "#4B1D6D",
      accentBg: "linear-gradient(135deg, #EFE4F7 0%, #B282E0 100%)",
      prompts: [
        { icon: "eye", label: "Visual", think: "Think about the person you could become if you completely trusted your financial potential. Hold that thought while you take your selfie.", q1: "What emotion did you capture?", q2: "What do you think is stopping you from becoming that person today?" },
        { icon: "ear", label: "Auditory", think: "Think about the belief about money that has influenced your life the most. Hold that thought while you take your selfie.", q1: "If you stopped believing it today, what would change?", q2: "What new belief would you choose instead?" },
        { icon: "hand", label: "Kinesthetic", think: "Think about living with complete confidence in your financial future. Hold that thought while you take your selfie.", q1: "Where did you feel confidence?", q2: "Where did you still feel resistance, and why?" },
        { icon: "wind", label: "Olfactory", think: "Think about the moment you felt most proud of yourself. Hold that thought while you take your selfie.", q1: "What scent came to mind?", q2: "What does that memory remind you about your own potential?" },
        { icon: "sparkles", label: "Taste", think: "Think about the life you're creating through your financial choices. Hold that thought while you take your selfie.", q1: "What flavour best represents that life?", q2: "What does that flavour reveal about who you're becoming?" },
        { icon: "key", label: "Intuitive", think: "Think about the future version of yourself that already exists. Hold that thought while you take your selfie.", q1: "What is that version of you trying to teach you today?", q2: "What is one belief you're ready to leave behind?" },
      ],
    },
  ],
  seoTitle: "Financial Freedom Snapshot | SnapshotFame",
  seoDescription: "Your private Financial Freedom Snapshot experience by Dawn Delisle.",
  bookHeadline: "60-Minute Life Clarity Reading",
  bookPrice: "$150",
  bookBody: "Sometimes the biggest breakthrough comes when someone helps connect the pieces you cannot quite see yourself.",
  bookHref: "/sessions/truth-session",
};

const LOVE_CONNECTION = {
  productId: "love-connection",
  storageKey: "sf_lc",
  brand: "Love Connection",
  headline: "Love Connection",
  subheadline: "Snapshot",
  welcomeH1: <>Your <em>Love Connection</em> Snapshot</>,
  welcomeQuote: "One reflection. One truth. Everything shifts.",
  welcomeBody: [
    "You are about to enter a private mirror for how you open, bond, and protect your heart. Each petal will help you uncover the invisible thread running through your relationships — the story your heart has been trying to show you.",
  ],
  petals: [
    {
      id: "rose",
      color: "Soft Rose",
      theme: "Self-Love · Worth · Belonging",
      accent: "#F4A6C4",
      accentDeep: "#B8547E",
      accentBg: "linear-gradient(135deg, #FFE4EE 0%, #F4A6C4 55%, #B8547E 100%)",
      prompts: [
        { icon: "eye", label: "Visual", think: "Think about how you see yourself when you're completely alone. Hold that thought while you take your selfie.", q1: "What emotion did you capture?", q2: "Is that the way you would want someone who loves you to see you?" },
        { icon: "ear", label: "Auditory", think: "Think about the voice inside you that speaks the loudest when you're being hard on yourself. Hold that thought while you take your selfie.", q1: "Whose voice does it sound like?", q2: "What would change if you stopped listening to it?" },
        { icon: "hand", label: "Kinesthetic", think: "Think about being fully accepted, exactly as you are, by yourself. Hold that thought while you take your selfie.", q1: "Where did that feeling live in your body?", q2: "What part of you has been waiting to hear that?" },
        { icon: "wind", label: "Olfactory", think: "Think about a moment when you felt completely at home in yourself. Hold that thought while you take your selfie.", q1: "What scent came with that memory?", q2: "What does that memory remind you that you already are?" },
        { icon: "sparkles", label: "Taste", think: "Think about what self-love would taste like if you finally gave it to yourself. Hold that thought while you take your selfie.", q1: "What flavour did you choose?", q2: "What does that flavour reveal about the love you've been withholding from yourself?" },
        { icon: "key", label: "Intuitive", think: "Look at your selfie. What is the part of you that no one else has fully seen? Hold that thought.", q1: "What has that part been quietly asking for?", q2: "What would happen if you finally gave it to yourself?" },
      ],
    },
    {
      id: "ruby",
      color: "Deep Ruby",
      theme: "Attraction · Desire · The Pull",
      accent: "#C0143C",
      accentDeep: "#7A0824",
      accentBg: "linear-gradient(135deg, #FADCE2 0%, #C0143C 55%, #7A0824 100%)",
      prompts: [
        { icon: "eye", label: "Visual", think: "Think about the type of person you keep being drawn to — the same face in different bodies. Hold that thought while you take your selfie.", q1: "What emotion did you capture?", q2: "What do you think you're actually reaching for through them?" },
        { icon: "ear", label: "Auditory", think: "Think about the story you tell yourself when you meet someone who feels magnetic. Hold that thought while you take your selfie.", q1: "Where did that story first come from?", q2: "Is it a story of love, or a story of proving something?" },
        { icon: "hand", label: "Kinesthetic", think: "Think about the last time you felt real chemistry with someone. Hold that thought while you take your selfie.", q1: "Where did that pull live in your body?", q2: "Was it excitement — or was it something older asking to be resolved?" },
        { icon: "wind", label: "Olfactory", think: "Think about the scent of someone you couldn't stop thinking about. Hold that memory while you take your selfie.", q1: "What scent came back to you?", q2: "What did that person awaken in you that you'd been missing in yourself?" },
        { icon: "sparkles", label: "Taste", think: "Think about what falling in love has tasted like for you — sweet, bitter, forbidden, familiar. Hold that thought while you take your selfie.", q1: "What flavour rose up first?", q2: "What does that flavour reveal about the kind of love you keep choosing?" },
        { icon: "key", label: "Intuitive", think: "Look at your selfie. What is your heart actually attracted to — and what is your wound attracted to? Hold that thought.", q1: "Which one has been leading?", q2: "What would love look like if your wound stopped picking?" },
      ],
    },
    {
      id: "pearl",
      color: "Pearlized White",
      theme: "Reflection · Truth · The Mirror",
      accent: "#E8DFD3",
      accentDeep: "#8A7E6D",
      accentBg: "linear-gradient(135deg, #FBF7F0 0%, #E8DFD3 55%, #8A7E6D 100%)",
      prompts: [
        { icon: "eye", label: "Visual", think: "Think about the pattern that keeps repeating in every relationship you've had. Hold that thought while you take your selfie.", q1: "What emotion did you capture?", q2: "What is that pattern trying to teach you — not punish you for?" },
        { icon: "ear", label: "Auditory", think: "Think about the words you wish someone had said to you — but never did. Hold that thought while you take your selfie.", q1: "Whose voice were you waiting to hear them from?", q2: "Could you say those words to yourself today?" },
        { icon: "hand", label: "Kinesthetic", think: "Think about how you feel in your body when love is safe. Hold that thought while you take your selfie.", q1: "How would you describe that feeling in one word?", q2: "How often do you actually let yourself feel that?" },
        { icon: "wind", label: "Olfactory", think: "Think about the scent of a moment when you felt truly loved — no performance, no earning it. Hold that thought while you take your selfie.", q1: "What scent returned to you?", q2: "What does that memory show you about the love you're worthy of?" },
        { icon: "sparkles", label: "Taste", think: "Think about what the love you're ready for would taste like. Hold that thought while you take your selfie.", q1: "What flavour came forward?", q2: "What does that flavour reveal about how you want to be received?" },
        { icon: "key", label: "Intuitive", think: "Look at your selfie. What is the truth about love you've been avoiding? Hold that thought.", q1: "Why do you think you've been avoiding it?", q2: "What would change the moment you stopped avoiding it?" },
      ],
    },
  ],
  seoTitle: "Love Connection Snapshot | SnapshotFame",
  seoDescription: "Your private Love Connection Snapshot experience by Dawn Delisle.",
  bookHeadline: "60-Minute Life Clarity Reading",
  bookPrice: "$150",
  bookBody: "If your reflection uncovered something tender, Dawn can help you hold it — and see the next step clearly.",
  bookHref: "/sessions/truth-session",
};

const SIX_COLOR_COMPLETE = {
  productId: "six-color-complete",
  storageKey: "sf_6c",
  brand: "6-Color Complete",
  headline: "6-Color Complete",
  subheadline: "Snapshot",
  welcomeH1: <>Your <em>6-Color Complete</em> Snapshot</>,
  welcomeQuote: "Six colors. Six senses. One clear reflection.",
  welcomeBody: [
    "You are about to walk through six petals — one for each of your senses. Each color holds its own theme, its own truth, and its own invitation.",
    "Answer each question with Memory and Meaning. After you finish a color, you'll be invited to pause, read your reflections back, and take a few selfies while you hold that color's energy — before you move to the next.",
  ],
  reviewNote: "After all six colors, review the full picture. Look for the pattern only you can see.",
  showSelfiePause: true,
  petals: [
    {
      id: "visual",
      color: "Yellow / Gold",
      theme: "Imagination · Clarity · Manifestation",
      accent: "#EFC229",
      accentDeep: "#8B6914",
      accentBg: "linear-gradient(135deg, #FFF3D1 0%, #EFC229 55%, #8B6914 100%)",
      nextLabel: "Complete Visual",
      selfieMsg: "Read your Visual answers back to yourself. See yourself already there. Take a few selfies while you hold this golden light — this is what clarity looks like on you.",
      prompts: [
        { icon: "eye", mainQ: "I want to see myself achieving…", q1: "What is the first image that comes to mind?", q2: "What does that image reveal about what you truly want?" },
        { icon: "eye", mainQ: "Visualize the environment where your dreams unfold.", q1: "Have you ever experienced a place that felt similar?", q2: "What does that environment represent to you?" },
        { icon: "eye", mainQ: "Picture yourself surrounded by…", q1: "Who or what immediately appears in your vision?", q2: "Why do those people or surroundings matter to you?" },
        { icon: "eye", mainQ: "What does success look like to you?", q1: "What image appears first?", q2: "Why does that picture represent success for you?" },
        { icon: "eye", mainQ: "Describe the visual details of your ideal future.", q1: "Which detail stands out the most?", q2: "What do you think that detail is trying to show you?" },
        { icon: "eye", mainQ: "What symbols represent abundance to you?", q1: "Where have you seen that symbol before?", q2: "What does that symbol personally mean to you?" },
      ],
    },
    {
      id: "auditory",
      color: "Light Blue",
      theme: "Voice · Expression · Inspiration",
      accent: "#7DB9E8",
      accentDeep: "#2E5C87",
      accentBg: "linear-gradient(135deg, #DCEBF8 0%, #7DB9E8 55%, #2E5C87 100%)",
      nextLabel: "Complete Auditory",
      selfieMsg: "Read your Auditory answers back — out loud if you can. Hear the voice you've been growing. Take a few selfies while you hold this sound.",
      prompts: [
        { icon: "ear", mainQ: "What words do you want to hear about your success?", q1: "What would hearing those words mean to you?", q2: "Who do you hope will say them?" },
        { icon: "ear", mainQ: "What music or sounds represent your achievement?", q1: "What memory or future vision comes to mind?", q2: "Why do those sounds feel meaningful?" },
        { icon: "ear", mainQ: "What affirmations do you listen to daily?", q1: "Which affirmation resonates with you most?", q2: "How does it influence your actions?" },
        { icon: "ear", mainQ: "What will others say when you succeed?", q1: "Who immediately comes to mind?", q2: "How do you imagine responding?" },
        { icon: "ear", mainQ: "How will you celebrate verbally?", q1: "What words will you say to yourself?", q2: "Who will you want to share your success with first?" },
        { icon: "ear", mainQ: "What sounds bring you joy and abundance?", q1: "Why do those sounds make you feel abundant?", q2: "How could you invite more of them into your life?" },
      ],
    },
    {
      id: "smell",
      color: "Green",
      theme: "Sensation · Memory · Connection",
      accent: "#6BB544",
      accentDeep: "#3A7A24",
      accentBg: "linear-gradient(135deg, #E4F3D9 0%, #6BB544 55%, #3A7A24 100%)",
      nextLabel: "Complete Smell",
      selfieMsg: "Read your Smell answers back and breathe deep. Let your body remember these scents. Take a few selfies while you hold this fragrance close.",
      prompts: [
        { icon: "wind", mainQ: "Fragrances that trigger feelings of abundance…", q1: "What fragrance instantly brings up a positive memory?", q2: "What does that scent remind you that is possible for you?" },
        { icon: "wind", mainQ: "Describe the smells in your ideal environment.", q1: "Have you ever been in a place that smelled like that?", q2: "What do those smells tell you about your dream life?" },
        { icon: "wind", mainQ: "What aromas help you feel confident?", q1: "When have you smelled this before?", q2: "What does this aroma give you the courage to do?" },
        { icon: "wind", mainQ: "Scents that remind you of achievement…", q1: "What scent brings back a moment of pride?", q2: "What does that moment of achievement mean to you?" },
        { icon: "wind", mainQ: "The air around you smells like…", q1: "What is the first smell you notice in your vision?", q2: "What does that air represent in your future life?" },
        { icon: "wind", mainQ: "How do smells influence your mood and energy?", q1: "What scent has lifted your mood in the past?", q2: "How can you use that scent to support your goals?" },
      ],
    },
    {
      id: "taste",
      color: "Orange",
      theme: "Celebration · Abundance · Achievement",
      accent: "#F08A2A",
      accentDeep: "#A45312",
      accentBg: "linear-gradient(135deg, #FBE0C6 0%, #F08A2A 55%, #A45312 100%)",
      nextLabel: "Complete Taste",
      selfieMsg: "Read your Taste answers back. Let the flavour of your dream life sit on your tongue. Take a few selfies while you hold this celebration.",
      prompts: [
        { icon: "sparkles", mainQ: "What taste represents celebration to you?", q1: "What memory comes to mind?", q2: "What does that memory tell you about what truly feels rewarding?" },
        { icon: "sparkles", mainQ: "What best describes the flavor of success?", q1: "What memory is connected to that flavor?", q2: "What does that memory reveal about your definition of success?" },
        { icon: "sparkles", mainQ: "What will you eat or drink to celebrate your achievement?", q1: "How does imagining that celebration make you feel?", q2: "What does that feeling tell you about the future you're building?" },
        { icon: "sparkles", mainQ: "What food makes you feel abundant?", q1: "How would it feel to enjoy that food without hesitation?", q2: "What does that tell you about the life you're ready to create?" },
        { icon: "sparkles", mainQ: "What taste reminds you of your goals?", q1: "What dream or achievement comes to mind?", q2: "How does that inspire you to keep moving forward?" },
        { icon: "sparkles", mainQ: "How do you savor the sweetness of accomplishment?", q1: "How can you pause and truly enjoy your achievements?", q2: "What does celebrating your success inspire you to accomplish next?" },
      ],
    },
    {
      id: "emotional",
      color: "Purple",
      theme: "Feelings · Awareness · Alignment",
      accent: "#8E44D4",
      accentDeep: "#4B1D6D",
      accentBg: "linear-gradient(135deg, #EFE4F7 0%, #8E44D4 55%, #4B1D6D 100%)",
      nextLabel: "Complete Emotional",
      selfieMsg: "Read your Emotional answers back and let yourself feel every single word. Take a few selfies while you hold this feeling — this is the frequency you're becoming.",
      prompts: [
        { icon: "key", mainQ: "How will achieving this goal make you feel?", q1: "What emotion are you looking forward to experiencing most?", q2: "How do you imagine that feeling changing your daily life?" },
        { icon: "key", mainQ: "Describe the emotions of living your dream life.", q1: "Which emotion stands out more than any other?", q2: "What do you think that emotion is trying to tell you?" },
        { icon: "key", mainQ: "What feelings do you want to experience every day?", q1: "What could you do today to experience more of that feeling?", q2: "How would your life change if that feeling became your new normal?" },
        { icon: "key", mainQ: "How does abundance feel in your body?", q1: "Where do you notice that feeling first?", q2: "What does your body seem to be saying when you feel abundant?" },
        { icon: "key", mainQ: "What emotional state helps you manifest best?", q1: "When have you felt that way before?", q2: "How could you intentionally create that state more often?" },
        { icon: "key", mainQ: "How do you connect to the feeling of already having achieved this?", q1: "What helps you step into that feeling most easily?", q2: "How does thinking this way change your decisions today?" },
      ],
    },
    {
      id: "touch",
      color: "Red / Pink",
      theme: "Sensation · Embodiment · Presence",
      accent: "#E6607A",
      accentDeep: "#A02540",
      accentBg: "linear-gradient(135deg, #FADCE2 0%, #E6607A 55%, #A02540 100%)",
      nextLabel: "Complete Touch",
      selfieMsg: "Read your Touch answers back and place your hand on your heart. Feel yourself already living it. Take a few selfies while you hold this presence — this is your embodiment.",
      prompts: [
        { icon: "hand", mainQ: "How does your body feel when you're successful?", q1: "When have you felt this feeling before?", q2: "What is your body trying to tell you?" },
        { icon: "hand", mainQ: "What textures represent luxury and abundance to you?", q1: "What is the first texture that comes to mind?", q2: "Why does that texture symbolize abundance for you?" },
        { icon: "hand", mainQ: "What physical sensations do you associate with your dream life?", q1: "Have you experienced that feeling before?", q2: "What does that sensation reveal about the life you're creating?" },
        { icon: "hand", mainQ: "How do you carry yourself when you're confident?", q1: "When have you felt this version of yourself?", q2: "What changes when you embody that confidence?" },
        { icon: "hand", mainQ: "What will you physically touch when you achieve this?", q1: "What object or place immediately comes to mind?", q2: "Why is that milestone important to you?" },
        { icon: "hand", mainQ: "Describe the physical environment of your success.", q1: "What place do you picture first?", q2: "What does that environment represent about your future?" },
      ],
    },
  ],
  seoTitle: "6-Color Complete Snapshot | SnapshotFame",
  seoDescription: "Your private 6-Color Complete Snapshot — all six senses, all six colors, one full reflection by Dawn Delisle.",
  bookHeadline: "60-Minute Life Clarity Reading",
  bookPrice: "$150",
  bookBody: "You just walked through the full spectrum. If you'd like Dawn to help you connect the pattern, this is where clarity turns into action.",
  bookHref: "/sessions/truth-session",
};

const DREAM_SHARED_SENSES = [
  {
    icon: "eye",
    label: "Visual",
    mainQ: "When everything is quiet, what image keeps returning?",
    selfie: "Get quiet for a moment. Close your eyes and let that image rise. Hold it while you take your selfie.",
    q1: "Write the very first image that comes to mind.",
    q2: "Describe what you see in as much detail as possible.",
    q3: "What do you feel this image has been trying to show you?",
  },
  {
    icon: "ear",
    label: "Auditory",
    mainQ: "If you could silence one inner voice, what would it stop saying?",
    selfie: "Listen inward for a moment. Let the loudest voice come forward. Hold that voice while you take your selfie.",
    q1: "Write the exact words you hear in your mind.",
    q2: "When do you hear this voice most often?",
    q3: "If that voice disappeared tomorrow, what would you finally do?",
  },
  {
    icon: "hand",
    label: "Kinesthetic",
    mainQ: "When you think about a recurring problem, where do you feel it in your body?",
    selfie: "Bring that recurring problem to mind. Notice where your body holds it. Hold that sensation while you take your selfie.",
    q1: "Describe exactly where you feel it.",
    q2: "What does the sensation feel like? (Tight, heavy, burning, numb, pressure, etc.)",
    q3: "If that sensation could speak, what would it say?",
  },
  {
    icon: "wind",
    label: "Olfactory",
    mainQ: "What scent instantly transports you back to a moment you wish you could forget?",
    selfie: "Let that scent rise up in your memory. Hold that memory while you take your selfie.",
    q1: "Write the first scent that comes to mind.",
    q2: "What memory, person, or experience do you connect with that scent?",
    q3: "What pattern do you notice when you reflect on that memory?",
  },
  {
    icon: "sparkles",
    label: "Taste",
    mainQ: "What taste do you avoid, and what does it remind you of?",
    selfie: "Let that flavour come forward on your tongue. Hold that taste while you take your selfie.",
    q1: "Write the first taste that comes to mind.",
    q2: "What memory, person, or experience do you connect with that taste?",
    q3: "What pattern do you notice when you reflect on that memory?",
  },
];

const DREAM_CONNECTION = {
  productId: "dream-connection",
  storageKey: "sf_dc",
  brand: "Dream Connection",
  headline: "Dream Connection",
  subheadline: "Snapshot",
  welcomeH1: <>Your <em>Dream Connection</em> Snapshot</>,
  welcomeQuote: "Your intuition has been whispering. It's time to listen.",
  welcomeBody: [
    "You are about to walk through three petals — each one a doorway into a different part of your inner knowing. Every sense holds a whisper. Every whisper holds a truth.",
    "Answer each sense with a question, a memory, and a meaning. After you finish a petal, you'll pause, read your reflections back, and take a few selfies while you hold that color's energy — before moving on.",
  ],
  reviewNote: "After all three petals, review the full picture. Look for the pattern your intuition has been drawing for you.",
  showSelfiePause: true,
  petals: [
    {
      id: "inner-clarity",
      name: "Inner Clarity",
      color: "Blue / Lavender",
      theme: "Clarity · Awareness · Inner Wisdom",
      accent: "#8CA0E0",
      accentDeep: "#4A5AA8",
      accentBg: "linear-gradient(135deg, #DDE4F5 0%, #8CA0E0 55%, #4A5AA8 100%)",
      nextLabel: "Complete Inner Clarity",
      selfieMsg: "Read your Inner Clarity reflections back to yourself, slowly. Notice the truth already living in your body. Take a few selfies while you hold this quiet, knowing light.",
      prompts: [
        ...DREAM_SHARED_SENSES,
        {
          icon: "key",
          label: "Intuitive",
          mainQ: "What breakthrough keeps telling you you're ready for more?",
          selfie: "Think about the breakthrough already waiting for you. Hold that knowing while you take your selfie.",
          q1: "Write the very first breakthrough that comes to mind.",
          q2: "What changed in you after that moment?",
          q3: "What do you think this breakthrough has been trying to show you about your future?",
        },
      ],
    },
    {
      id: "success-acceleration",
      name: "Success Acceleration",
      color: "Vibrant Green / Gold",
      theme: "Success · Momentum · Expansion",
      accent: "#7ABF3F",
      accentDeep: "#3F6E1E",
      accentBg: "linear-gradient(135deg, #E5F4D6 0%, #7ABF3F 55%, #3F6E1E 100%)",
      nextLabel: "Complete Success Acceleration",
      selfieMsg: "Read your Success Acceleration reflections back. Feel the momentum already moving through you. Take a few selfies while you hold this vibrant, expanding energy.",
      prompts: [
        ...DREAM_SHARED_SENSES,
        {
          icon: "key",
          label: "Intuitive",
          mainQ: "What flashes into your mind before you talk yourself out of it?",
          selfie: "Think about the flash you keep dismissing. Hold that spark while you take your selfie.",
          q1: "Write the first intuitive hit that comes to mind.",
          q2: "What truth is trying to break through?",
          q3: "What is your heart trying to remind you of?",
        },
      ],
    },
    {
      id: "connection-amplifier",
      name: "Connection Amplifier",
      color: "Warm Rose / Coral",
      theme: "Connection · Belonging · Heart Truth",
      accent: "#F0847C",
      accentDeep: "#B04E48",
      accentBg: "linear-gradient(135deg, #FBDEDA 0%, #F0847C 55%, #B04E48 100%)",
      nextLabel: "Complete Connection Amplifier",
      selfieMsg: "Read your Connection Amplifier reflections back. Place your hand on your heart. Take a few selfies while you hold this warm, rose-coral tenderness — this is what belonging feels like on you.",
      prompts: [
        {
          icon: "eye",
          label: "Visual",
          mainQ: "What connection keeps replaying in your mind, even after all this time?",
          selfie: "Think about that person. Let their face come forward. Hold that image while you take your selfie.",
          q1: "Write the very first person or moment that comes to mind.",
          q2: "Describe what you see in as much detail as possible.",
          q3: "What do you feel this connection has been trying to show you?",
        },
        {
          icon: "ear",
          label: "Auditory",
          mainQ: "What words do you still hear, long after they were spoken?",
          selfie: "Let those words come back to you. Hear them clearly. Hold that voice while you take your selfie.",
          q1: "Write the exact words or phrase that immediately comes to mind.",
          q2: "Who said them, and why has it stayed with you?",
          q3: "What do you feel this voice has been trying to show you?",
        },
        {
          icon: "hand",
          label: "Kinesthetic",
          mainQ: "When you think about someone you love — or someone you've lost — where do you feel that connection in your body?",
          selfie: "Think of that person now. Notice where love lives in your body. Hold that feeling while you take your selfie.",
          q1: "Describe exactly where you feel it.",
          q2: "What does the sensation feel like? (Warm, tight, open, aching, heavy, expansive, etc.)",
          q3: "If that feeling could speak, what would it be trying to remind you?",
        },
        {
          icon: "wind",
          label: "Olfactory",
          mainQ: "What scent reminds you of a moment you felt you truly belonged?",
          selfie: "Let that scent rise around you. Breathe it in. Hold that belonging while you take your selfie.",
          q1: "Write the first scent that comes to mind.",
          q2: "Who or what memory do you associate with that scent?",
          q3: "What do you feel this memory has been trying to show you?",
        },
        {
          icon: "sparkles",
          label: "Taste",
          mainQ: "If one meal could tell the story of an important relationship, what would it be?",
          selfie: "Let that meal come to mind — the flavour, the table, the person. Hold that memory while you take your selfie.",
          q1: "Write the first meal that comes to mind.",
          q2: "Who did you share it with, and what made that moment unforgettable?",
          q3: "What do you feel this memory has been trying to show you?",
        },
        {
          icon: "key",
          label: "Intuitive",
          mainQ: "What truth about relationships have you always known, but only recently understood?",
          selfie: "Think about the truth you've finally let yourself see. Hold that knowing while you take your selfie.",
          q1: "Write the very first truth that comes to mind.",
          q2: "What experience helped you understand it?",
          q3: "How will this truth influence your relationships moving forward?",
        },
      ],
    },
  ],
  seoTitle: "Dream Connection Snapshot | SnapshotFame",
  seoDescription: "Your private Dream Connection Snapshot — follow the whisper of your intuition through three petals of clarity, success, and connection.",
  bookHeadline: "60-Minute Life Clarity Reading",
  bookPrice: "$150",
  bookBody: "If your intuition surfaced something you're ready to move on, Dawn can help you translate the whisper into your next step.",
  bookHref: "/sessions/truth-session",
};

const ICON_MAP = { eye: Eye, ear: Ear, hand: Hand, wind: Wind, sparkles: Sparkles, key: Key };

// ================= SHARED SUB-COMPONENTS =================
function PetalArt({ id }) {
  return (
    <svg viewBox="0 0 200 260" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={`petal-${id}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="var(--petal-light)" />
          <stop offset="60%" stopColor="var(--petal-mid)" />
          <stop offset="100%" stopColor="var(--petal-dark)" />
        </linearGradient>
        <radialGradient id={`sheen-${id}`} cx="35%" cy="30%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.7)" />
          <stop offset="70%" stopColor="rgba(255,255,255,0)" />
        </radialGradient>
      </defs>
      <path d="M100 15 C 145 55, 175 130, 155 210 C 140 240, 115 250, 100 250 C 85 250, 60 240, 45 210 C 25 130, 55 55, 100 15 Z" fill={`url(#petal-${id})`} stroke="rgba(0,0,0,0.15)" strokeWidth="1.5" />
      <path d="M100 30 C 100 70, 100 150, 100 240" stroke="rgba(0,0,0,0.12)" strokeWidth="1" fill="none" />
      <path d="M100 60 C 110 80, 125 130, 130 190" stroke="rgba(0,0,0,0.08)" strokeWidth="0.8" fill="none" />
      <path d="M100 60 C 90 80, 75 130, 70 190" stroke="rgba(0,0,0,0.08)" strokeWidth="0.8" fill="none" />
      <path d="M100 20 C 145 60, 170 130, 150 200 C 135 220, 115 230, 100 230" fill={`url(#sheen-${id})`} />
    </svg>
  );
}

function AccessGate({ config, onUnlock }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const r = await axios.post(`${API}/snapshot/verify`, { code: code.trim(), product: config.productId });
      if (r.data.valid) {
        localStorage.setItem(`${config.storageKey}_access`, code.trim());
        onUnlock();
      } else {
        setError("This code doesn't match. Please check your welcome email from Dawn.");
      }
    } catch (e) {
      setError("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 pt-24 pb-16" data-testid="access-gate">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6"><Lock className="text-[#F5D48A]" size={44} /></div>
          <div className="section-label">Private Access</div>
          <h1 className="display-serif text-4xl sm:text-5xl mb-3">{config.headline}<br/><em>{config.subheadline}</em></h1>
          <p className="text-[#F2EFE8]/60 text-sm">Enter your access code to begin your private reflection experience.</p>
        </div>
        <form onSubmit={submit} className="sf-card jewel-card jewel-gold rounded-sm p-8">
          <label className="form-label">Your Access Code</label>
          <input required autoFocus data-testid="access-code-input" className="form-input mb-4 tracking-widest text-center uppercase" placeholder="XXXX-XXXX" value={code} onChange={(e) => setCode(e.target.value)} />
          {error && <p className="text-rose-300 text-sm mb-4" data-testid="access-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="access-submit" className="btn-gold w-full">{loading ? "Unlocking..." : "Enter"}</button>
          <p className="text-[10px] text-center text-[#A89FA8] mt-5 tracking-widest uppercase">Code arrives after purchase</p>
        </form>
      </div>
    </div>
  );
}

function Prompt({ prompt, petal, value, onChange, index }) {
  const Icon = ICON_MAP[prompt.icon];
  const hasThree = !!prompt.q3;
  const isMemMean = !!prompt.mainQ && !hasThree;

  if (hasThree) {
    // Dream Connection: sense label + main question + 3 sub-fields
    return (
      <div className="mb-8 rounded-sm border-l-4 pl-6 pr-4 py-5" style={{ borderColor: petal.accent, background: "rgba(21,11,32,0.4)" }}>
        <div className="flex items-center gap-3 mb-3">
          {Icon && <Icon size={22} style={{ color: petal.accent }} />}
          <h4 className="serif text-xl uppercase tracking-widest" style={{ color: petal.accent }}>{prompt.label}</h4>
        </div>
        <p className="serif italic text-lg text-[#F2EFE8]/95 mb-4 leading-snug">{prompt.mainQ}</p>
        {prompt.selfie && (
          <div className="flex items-start gap-3 mb-5 rounded-sm border pl-3 pr-4 py-3" style={{ borderColor: `${petal.accent}55`, background: `${petal.accent}11` }}>
            <Camera size={16} className="mt-1 flex-shrink-0" style={{ color: petal.accent }} />
            <p className="text-[#F2EFE8]/85 text-sm leading-relaxed italic">{prompt.selfie}</p>
          </div>
        )}
        <div className="space-y-4">
          <div>
            <label className="form-label" style={{ color: petal.accent }}>{prompt.q1}</label>
            <textarea className="form-input" rows={2} value={value.a1 || ""} onChange={(e) => onChange({ ...value, a1: e.target.value })} placeholder="Take your time..." />
          </div>
          <div>
            <label className="form-label" style={{ color: petal.accent }}>{prompt.q2}</label>
            <textarea className="form-input" rows={2} value={value.a2 || ""} onChange={(e) => onChange({ ...value, a2: e.target.value })} placeholder="Write what comes..." />
          </div>
          <div>
            <label className="form-label" style={{ color: petal.accent }}>{prompt.q3}</label>
            <textarea className="form-input" rows={2} value={value.a3 || ""} onChange={(e) => onChange({ ...value, a3: e.target.value })} placeholder="What the meaning is..." />
          </div>
        </div>
      </div>
    );
  }

  if (isMemMean) {
    // 6-Color Complete: Memory + Meaning format
    return (
      <div className="mb-8 rounded-sm border-l-4 pl-6 pr-4 py-5" style={{ borderColor: petal.accent, background: "rgba(21,11,32,0.4)" }}>
        <div className="flex items-start gap-3 mb-3">
          <span className="serif text-2xl leading-none mt-0.5" style={{ color: petal.accent }}>{index + 1}.</span>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              {Icon && <Icon size={18} style={{ color: petal.accent }} />}
              <h4 className="serif text-xl text-[#F2EFE8]/95 leading-snug">{prompt.mainQ}</h4>
            </div>
          </div>
        </div>
        <div className="space-y-4 mt-4">
          <div>
            <label className="form-label" style={{ color: petal.accent }}>
              <span className="font-semibold">Memory:</span> {prompt.q1}
            </label>
            <textarea className="form-input" rows={2} value={value.a1 || ""} onChange={(e) => onChange({ ...value, a1: e.target.value })} placeholder="A memory arises..." />
          </div>
          <div>
            <label className="form-label" style={{ color: petal.accent }}>
              <span className="font-semibold">Meaning:</span> {prompt.q2}
            </label>
            <textarea className="form-input" rows={2} value={value.a2 || ""} onChange={(e) => onChange({ ...value, a2: e.target.value })} placeholder="What that memory whispers..." />
          </div>
        </div>
      </div>
    );
  }

  // Financial / Love: Think + selfie format
  return (
    <div className="mb-8 rounded-sm border-l-4 pl-6 py-4" style={{ borderColor: petal.accent, background: "rgba(21,11,32,0.4)" }}>
      <div className="flex items-center gap-3 mb-3">
        <Icon size={22} style={{ color: petal.accent }} />
        <h4 className="serif text-2xl" style={{ color: petal.accent }}>{prompt.label}</h4>
      </div>
      <p className="text-[#F2EFE8]/85 mb-4 leading-relaxed">{prompt.think}</p>
      <div className="space-y-4">
        <div>
          <label className="form-label" style={{ color: petal.accent }}>{prompt.q1}</label>
          <textarea className="form-input" rows={2} value={value.a1 || ""} onChange={(e) => onChange({ ...value, a1: e.target.value })} placeholder="Take your time..." />
        </div>
        <div>
          <label className="form-label" style={{ color: petal.accent }}>{prompt.q2}</label>
          <textarea className="form-input" rows={2} value={value.a2 || ""} onChange={(e) => onChange({ ...value, a2: e.target.value })} placeholder="Write what comes..." />
        </div>
      </div>
    </div>
  );
}

function PetalPage({ petal, brand, idx, total, answers, onAnswer, onNext, onBack, isLast }) {
  const complete = petal.prompts.filter((_, i) => answers[i]?.a1 || answers[i]?.a2).length;

  return (
    <div className="min-h-screen pt-24 pb-16 px-6 lg:px-12" data-testid={`petal-${petal.id}`}
      style={{ "--petal-light": petal.accent, "--petal-mid": petal.accent, "--petal-dark": petal.accentDeep }}>
      <div className="max-w-3xl mx-auto">
        {/* Progress */}
        <div className="flex items-center justify-between mb-8 text-xs tracking-widest uppercase text-[#A89FA8]">
          <span>Petal {idx + 1} of {total}</span>
          <div className="flex gap-2">
            {Array.from({ length: total }).map((_, i) => (
              <span key={i} className="h-1 w-8 rounded-full" style={{ background: i <= idx ? petal.accent : "rgba(181,139,46,0.2)" }}></span>
            ))}
          </div>
          <span>{complete} / 6 reflected</span>
        </div>

        {/* Petal Header */}
        <div className="rounded-sm p-8 mb-8 text-center relative overflow-hidden" style={{ background: petal.accentBg }}>
          <div className="absolute inset-0 bg-black/25"></div>
          <div className="relative z-10">
            <div className="text-[10px] tracking-[0.3em] uppercase mb-2 text-white/90" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.5)" }}>{brand}</div>
            <h2 className="serif text-3xl sm:text-4xl mb-6 text-white" style={{ textShadow: "0 2px 6px rgba(0,0,0,0.5)" }}>{petal.name || petal.color} Petal</h2>
            <div className="w-40 h-52 mx-auto mb-6">
              <PetalArt id={petal.id} />
            </div>
            <p className="text-lg font-medium text-white" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}>Theme: {petal.theme}</p>
          </div>
        </div>

        {/* Prompts */}
        {petal.prompts.map((p, i) => (
          <Prompt key={i} prompt={p} petal={petal} index={i} value={answers[i] || {}} onChange={(v) => onAnswer(i, v)} />
        ))}

        {/* Nav */}
        <div className="flex justify-between mt-10">
          {idx > 0 ? (
            <button onClick={onBack} className="btn-outline-gold flex items-center gap-2" data-testid={`back-${petal.id}`}>
              <ArrowLeft size={14} /> Previous Petal
            </button>
          ) : <div />}
          <button onClick={onNext} className="btn-gold flex items-center gap-2" data-testid={`next-${petal.id}`}>
            {isLast ? "See Your Reflection" : (petal.nextLabel || "Next Petal")} <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

function WelcomeScreen({ config, onStart }) {
  return (
    <div className="min-h-screen flex items-center justify-center pt-24 pb-16 px-6" data-testid="welcome-screen">
      <div className="max-w-2xl w-full text-center">
        <div className="section-label" data-testid="welcome-label">Welcome</div>
        <h1 className="display-serif text-5xl sm:text-6xl mb-8" data-testid="welcome-h1">
          {config.welcomeH1}
        </h1>
        <p className="serif italic text-2xl text-[#F5D48A] mb-8">{config.welcomeQuote}</p>
        <div className="sf-card jewel-card jewel-gold rounded-sm p-8 mb-8 text-left space-y-4 text-[#F2EFE8]/85">
          {config.welcomeBody.map((line, i) => <p key={i}>{line}</p>)}
          <p className="font-semibold text-[#F5D48A]">There are no right or wrong answers — only honest ones.</p>
          <div className="pt-4 border-t border-[#B58B2E]/25">
            <div className="section-label mb-3">How to Use Your Snapshot</div>
            <p><strong className="text-[#F5D48A]">Completing Each Petal:</strong> Read each colored petal prompt carefully. Take your time. Trust your first thoughts.</p>
            <p><strong className="text-[#F5D48A]">Journaling:</strong> Write your reflections right on this page. Your answers save automatically as you go — return anytime.</p>
            <p><strong className="text-[#F5D48A]">Review & Discover:</strong> {config.reviewNote || "After all three petals, review your answers. Look for patterns and insights."}</p>
            {config.welcomeExtra && <p className="pt-2 text-[#F5D48A]/90 italic">{config.welcomeExtra}</p>}
          </div>
        </div>
        <button onClick={onStart} className="btn-gold" data-testid="welcome-begin">Begin My Journey</button>
      </div>
    </div>
  );
}

function CompleteScreen({ config, answers, onRestart }) {
  return (
    <div className="min-h-screen pt-24 pb-16 px-6 lg:px-12" data-testid="complete-screen">
      <div className="max-w-3xl mx-auto text-center">
        <div className="flex justify-center mb-6"><Check className="text-[#F5D48A]" size={48} /></div>
        <div className="section-label">Complete</div>
        <h1 className="display-serif text-5xl sm:text-6xl mb-6">Your <em>Reflection</em></h1>
        <p className="text-[#F2EFE8]/70 text-lg mb-10 max-w-xl mx-auto">
          Look for patterns, themes, and insights that emerge. These reflections will guide you toward clarity and action.
        </p>

        {/* Interpretation Guide — the "how to spot your loops" section */}
        <div className="sf-card jewel-card jewel-gold rounded-sm p-8 mb-10 text-left">
          <div className="section-label mb-3">How to Read Your Snapshot</div>
          <h3 className="serif text-3xl gold-shimmer mb-5">Look for Your Loops</h3>
          <p className="text-[#F2EFE8]/85 mb-5 leading-relaxed">
            Your reflections below are more than answers — they are <em>mirrors</em>. The magic isn&apos;t in any single answer. It&apos;s in the <strong className="text-[#F5D48A]">patterns that repeat</strong> across all {config.petals.length === 6 ? "six" : "three"} petals.
          </p>
          <div className="space-y-5">
            <div>
              <p className="serif text-xl text-[#F5D48A] mb-2">1. Notice Your Recurring Words</p>
              <p className="text-[#F2EFE8]/80 leading-relaxed text-sm">Read your answers across all {config.petals.length === 6 ? "six" : "three"} petals. Which <em>words</em>, <em>emotions</em>, <em>beliefs</em>, and <em>themes</em> keep showing up? Which <em>images</em> return? These are your mirror anchors — recurring truths about you.</p>
            </div>
            <div>
              <p className="serif text-xl text-[#F5D48A] mb-2">2. Compare Your Energy Between Petals</p>
              <p className="text-[#F2EFE8]/80 leading-relaxed text-sm">If you took selfies with each petal — set them side by side. Notice how your <em>body language</em> shifted. Guarded → open? Tired → radiant? Small → grounded? Your body always tells the truth first.</p>
            </div>
            <div>
              <p className="serif text-xl text-[#F5D48A] mb-2">3. Find the Thread</p>
              <p className="text-[#F2EFE8]/80 leading-relaxed text-sm">Look for one belief, one story, one feeling that shows up in <em>all {config.petals.length === 6 ? "six" : "three"} petals</em>. That thread is your <strong className="text-[#F5D48A]">loop</strong> — the pattern that has been quietly shaping your choices.</p>
            </div>
            <div>
              <p className="serif text-xl text-[#F5D48A] mb-2">4. Choose One Next Step</p>
              <p className="text-[#F2EFE8]/80 leading-relaxed text-sm">You don&apos;t have to fix your loop today. Just <em>see</em> it. Awareness is the whole gift. From here, one small choice can begin to change everything.</p>
            </div>
          </div>
          <p className="text-[#F2EFE8]/75 italic mt-6 text-center serif">
            &quot;You aren&apos;t being told who you are. You&apos;re seeing it yourself.&quot;
          </p>
        </div>

        {config.petals.map((petal, pIdx) => (
          <div key={petal.id} className="sf-card jewel-card rounded-sm p-8 mb-6 text-left" style={{ "--jewel": petal.accent }} data-testid={`recap-${petal.id}`}>
            <h3 className="serif text-3xl mb-4" style={{ color: petal.accent }}>{petal.name || petal.color} — {petal.theme}</h3>
            {petal.prompts.map((p, i) => {
              const a = answers[pIdx]?.[i] || {};
              if (!a.a1 && !a.a2 && !a.a3) return null;
              const hasThree = !!p.q3;
              const isMemMean = !!p.mainQ && !hasThree;
              const heading = p.mainQ || p.label;
              const q1Prefix = isMemMean ? "Memory · " : "";
              const q2Prefix = isMemMean ? "Meaning · " : "";
              return (
                <div key={i} className="mb-4 pl-4 border-l-2" style={{ borderColor: petal.accent }}>
                  <p className={(isMemMean || hasThree) ? "serif text-base text-[#F2EFE8]/95 mb-2" : "text-[10px] tracking-widest uppercase mb-1"} style={(isMemMean || hasThree) ? {} : { color: petal.accent }}>
                    {hasThree ? <><span className="uppercase tracking-widest text-xs" style={{ color: petal.accent }}>{p.label} · </span>{heading}</> : (isMemMean ? `${i + 1}. ${heading}` : heading)}
                  </p>
                  {a.a1 && <p className="text-[#F2EFE8]/85 mb-1"><span className="text-[#A89FA8] text-xs">{q1Prefix}{p.q1}</span><br/>{a.a1}</p>}
                  {a.a2 && <p className="text-[#F2EFE8]/85 mb-1"><span className="text-[#A89FA8] text-xs">{q2Prefix}{p.q2}</span><br/>{a.a2}</p>}
                  {a.a3 && <p className="text-[#F2EFE8]/85"><span className="text-[#A89FA8] text-xs">{p.q3}</span><br/>{a.a3}</p>}
                </div>
              );
            })}
          </div>
        ))}

        <div className="sf-card jewel-card jewel-gold rounded-sm p-8 mt-10 text-center">
          <div className="section-label">Ready to Go Deeper?</div>
          <h3 className="serif text-3xl gold-shimmer mb-3">{config.bookHeadline}</h3>
          <p className="serif text-4xl gold-shimmer mb-6">{config.bookPrice}</p>
          <p className="text-[#F2EFE8]/75 mb-6 max-w-lg mx-auto">{config.bookBody}</p>
          <a href={config.bookHref} className="btn-gold">Book with Dawn</a>
        </div>

        <button onClick={onRestart} className="btn-outline-gold mt-8" data-testid="restart">Start Over</button>
        <p className="text-[10px] text-[#A89FA8] mt-6 tracking-widest uppercase">✨ Your answers are saved on your device — return anytime</p>
      </div>
    </div>
  );
}

// ================= SELFIE PAUSE SCREEN =================
function SelfiePauseScreen({ petal, brand, idx, total, answers, onContinue, onBack, isLast }) {
  const petalAnswers = answers[idx] || {};
  return (
    <div className="min-h-screen pt-24 pb-16 px-6 lg:px-12" data-testid={`selfie-pause-${petal.id}`}>
      <div className="max-w-3xl mx-auto">
        {/* Progress */}
        <div className="flex items-center justify-between mb-8 text-xs tracking-widest uppercase text-[#A89FA8]">
          <span>Selfie Moment · {petal.name || petal.color}</span>
          <div className="flex gap-2">
            {Array.from({ length: total }).map((_, i) => (
              <span key={i} className="h-1 w-6 rounded-full" style={{ background: i <= idx ? petal.accent : "rgba(181,139,46,0.2)" }}></span>
            ))}
          </div>
          <span>Petal {idx + 1} of {total}</span>
        </div>

        {/* Big color banner */}
        <div className="rounded-sm p-10 mb-8 text-center relative overflow-hidden" style={{ background: petal.accentBg }}>
          <div className="absolute inset-0 bg-black/30"></div>
          <div className="relative z-10">
            <div className="text-[10px] tracking-[0.3em] uppercase mb-2 text-white/90" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.5)" }}>{brand}</div>
            <h2 className="serif text-4xl sm:text-5xl mb-3 text-white" style={{ textShadow: "0 2px 6px rgba(0,0,0,0.6)" }}>Selfie Moment</h2>
            <p className="text-white/95 text-lg italic mb-1" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}>Hold the {petal.name || petal.color} energy</p>
          </div>
        </div>

        {/* Instruction card */}
        <div className="sf-card jewel-card rounded-sm p-8 mb-8 text-center" style={{ "--jewel": petal.accent }}>
          <div className="section-label mb-3" style={{ color: petal.accent }}>Pause & Reflect</div>
          <h3 className="serif text-2xl text-[#F2EFE8] mb-4">Take a few selfies as you read your answers back to yourself.</h3>
          <p className="text-[#F2EFE8]/75 leading-relaxed max-w-xl mx-auto">
            {petal.selfieMsg || `Notice how you feel while you re-read your ${petal.color} reflections. Take a few selfies while you hold this energy — this is where the shift begins.`}
          </p>
        </div>

        {/* Read-back of their answers */}
        <div className="sf-card jewel-card rounded-sm p-8 mb-8 text-left" style={{ "--jewel": petal.accent }}>
          <h4 className="serif text-2xl mb-6" style={{ color: petal.accent }}>Your {petal.name || petal.color} Reflections</h4>
          {petal.prompts.map((p, i) => {
            const a = petalAnswers[i] || {};
            const hasThree = !!p.q3;
            const hasAnswer = a.a1 || a.a2 || a.a3;
            const label1 = hasThree ? p.q1 : "Memory";
            const label2 = hasThree ? p.q2 : "Meaning";
            const label3 = hasThree ? p.q3 : null;
            return (
              <div key={i} className="mb-5 pl-4 border-l-2" style={{ borderColor: petal.accent }}>
                <p className="serif text-lg text-[#F2EFE8]/95 mb-2">
                  {hasThree ? <><span className="uppercase tracking-widest text-xs mr-2" style={{ color: petal.accent }}>{p.label}</span>{p.mainQ}</> : <>{i + 1}. {p.mainQ || p.think || p.label}</>}
                </p>
                {a.a1 ? (
                  <p className="text-[#F2EFE8]/85 mb-1 text-sm"><span className="text-[#A89FA8] text-[10px] tracking-widest uppercase">{label1} · </span>{a.a1}</p>
                ) : null}
                {a.a2 ? (
                  <p className="text-[#F2EFE8]/85 mb-1 text-sm"><span className="text-[#A89FA8] text-[10px] tracking-widest uppercase">{label2} · </span>{a.a2}</p>
                ) : null}
                {label3 && a.a3 ? (
                  <p className="text-[#F2EFE8]/85 text-sm"><span className="text-[#A89FA8] text-[10px] tracking-widest uppercase">{label3} · </span>{a.a3}</p>
                ) : null}
                {!hasAnswer && <p className="text-[#A89FA8]/70 text-xs italic">— you left this one blank —</p>}
              </div>
            );
          })}
        </div>

        {/* Nav */}
        <div className="flex justify-between mt-10">
          <button onClick={onBack} className="btn-outline-gold flex items-center gap-2" data-testid={`selfie-back-${petal.id}`}>
            <ArrowLeft size={14} /> Edit Answers
          </button>
          <button onClick={onContinue} className="btn-gold flex items-center gap-2" data-testid={`selfie-continue-${petal.id}`}>
            {isLast ? "See Full Reflection" : "Continue to Next Color"} <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ================= GENERIC SNAPSHOT EXPERIENCE =================
function SnapshotExperience({ config }) {
  const [unlocked, setUnlocked] = useState(false);
  const [step, setStep] = useState("welcome"); // welcome | {idx, mode} | complete
  const [answers, setAnswers] = useState({}); // { petalIdx: { promptIdx: {a1, a2} } }

  const accessKey = `${config.storageKey}_access`;
  const answersKey = `${config.storageKey}_answers`;

  useEffect(() => {
    const saved = localStorage.getItem(accessKey);
    if (saved) {
      axios.post(`${API}/snapshot/verify`, { code: saved, product: config.productId })
        .then((r) => r.data.valid && setUnlocked(true))
        .catch(() => {});
    }
    const savedAnswers = localStorage.getItem(answersKey);
    if (savedAnswers) { try { setAnswers(JSON.parse(savedAnswers)); } catch(e) { /* ignore corrupt */ } }
  }, [accessKey, answersKey, config.productId]);

  useEffect(() => {
    localStorage.setItem(answersKey, JSON.stringify(answers));
  }, [answers, answersKey]);

  const setAnswer = (petalIdx, promptIdx, val) => {
    setAnswers((prev) => ({ ...prev, [petalIdx]: { ...(prev[petalIdx] || {}), [promptIdx]: val } }));
  };

  if (!unlocked) return <><SEO title={`${config.headline} Snapshot — Access | SnapshotFame`} description={`Enter your access code to begin your ${config.headline} Snapshot experience.`} /><AccessGate config={config} onUnlock={() => setUnlocked(true)} /></>;

  const total = config.petals.length;
  const isPetal = typeof step === "object" && step !== null && step.mode === "petal";
  const isSelfie = typeof step === "object" && step !== null && step.mode === "selfie";

  const startPetal = (i) => setStep({ mode: "petal", idx: i });
  const startSelfie = (i) => setStep({ mode: "selfie", idx: i });

  const handleNextFromPetal = (i) => {
    if (config.showSelfiePause) return startSelfie(i);
    if (i < total - 1) return startPetal(i + 1);
    setStep("complete");
  };

  const handleContinueFromSelfie = (i) => {
    if (i < total - 1) return startPetal(i + 1);
    setStep("complete");
  };

  return (
    <>
      <SEO title={config.seoTitle} description={config.seoDescription} />
      {step === "welcome" && <WelcomeScreen config={config} onStart={() => startPetal(0)} />}
      {isPetal && (
        <PetalPage
          petal={config.petals[step.idx]}
          brand={config.brand}
          idx={step.idx}
          total={total}
          answers={answers[step.idx] || {}}
          onAnswer={(i, v) => setAnswer(step.idx, i, v)}
          onNext={() => handleNextFromPetal(step.idx)}
          onBack={() => step.idx > 0 && (config.showSelfiePause ? startSelfie(step.idx - 1) : startPetal(step.idx - 1))}
          isLast={step.idx === total - 1}
        />
      )}
      {isSelfie && (
        <SelfiePauseScreen
          petal={config.petals[step.idx]}
          brand={config.brand}
          idx={step.idx}
          total={total}
          answers={answers}
          onContinue={() => handleContinueFromSelfie(step.idx)}
          onBack={() => startPetal(step.idx)}
          isLast={step.idx === total - 1}
        />
      )}
      {step === "complete" && <CompleteScreen config={config} answers={answers} onRestart={() => { setStep("welcome"); setAnswers({}); localStorage.removeItem(answersKey); }} />}
    </>
  );
}

// ================= EXPORTED EXPERIENCES =================
export const SNAPSHOT_CONFIGS = {
  "financial-freedom": FINANCIAL_FREEDOM,
  "love-connection": LOVE_CONNECTION,
  "six-color-complete": SIX_COLOR_COMPLETE,
  "dream-connection": DREAM_CONNECTION,
};

export function FinancialFreedomExperience() {
  return <SnapshotExperience config={FINANCIAL_FREEDOM} />;
}

export function LoveConnectionExperience() {
  return <SnapshotExperience config={LOVE_CONNECTION} />;
}

export function SixColorCompleteExperience() {
  return <SnapshotExperience config={SIX_COLOR_COMPLETE} />;
}

export function DreamConnectionExperience() {
  return <SnapshotExperience config={DREAM_CONNECTION} />;
}
